import { useRef, ReactNode, CSSProperties } from 'react';
import { useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';
import type { TestProps } from '@direct-frontend/components/testing';
import { usePress } from '@direct-frontend/components';

/**
 * Пропсы компонента `RowMeasurer`.
 */
interface RowMeasurerProps extends TestProps {
    /**
     * Компонент строки грида.
     */
    children?: ReactNode;
    /**
     * CSS-класс строки грида.
     */
    className?: string;
    /**
     * Функция пересчета виртуализации при изменении размеров строки
     */
    measure: (node: HTMLDivElement | null) => void;
    /**
     * Нужно ли измерять строку
     */
    shouldMeasure?: boolean;
    /**
     * Инлайн стили строки грида.
     */
    style?: CSSProperties;
    /**
     * Индекс строки
     */
    index: number;
    /**
     * Коллбек клика по строке
     */
    onClick?: VoidFunction;
}

export function RowMeasurer({
    className,
    children,
    measure,
    shouldMeasure = true,
    style,
    index,
    onClick,
    ...props
}: RowMeasurerProps) {
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const elementRef = useRef<HTMLDivElement | null>(null);

    const { pressProps } = usePress({ onPress: onClick });

    useIsomorphicLayoutEffect(() => {
        if (!shouldMeasure) return;

        const update = () => {
            /**
             * Так как высота ряда не ограничена, то до инициализации react-table строка рендерится с нулевой высотой.
             * Не передаем ссылку на элемент в `measure`, пока строка не отрендерится с контентом
             */
            if (elementRef.current?.getBoundingClientRect().height !== 0) {
                measure(elementRef.current);
            }
        };
        update();

        const resizeObserver = resizeObserverRef.current ? resizeObserverRef.current : new ResizeObserver(update);
        const element = elementRef.current;

        if (element !== null) {
            resizeObserver.observe(element);
        }

        resizeObserverRef.current = resizeObserver;

        return () => {
            resizeObserver.disconnect();
        };
    }, [index, shouldMeasure]);

    return (
        <div
            className={className}
            ref={elementRef}
            style={style}
            data-index={index}
            {...(onClick ? pressProps : {})}
            data-testid={props['data-testid']}
        >
            {children}
        </div>
    );
}
